import { useMemo, useState, useCallback, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import {
  Col,
  Row,
  Card,
  CardBody,
  Button,
  Spinner,
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap"

// Custom Components
import TableContainer from "../../../components/Common/TableContainer"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import DeleteModal from "../../../components/Common/DeleteModal"

// Custom Hooks
import {
  useDeleteVehicleStock,
  useEditVehicleStock,
  useVehicleStocks,
} from "hooks/stockHook"

import { useListParams } from "hooks/useListParams"
import ButtonTooltip from "components/Common/ButtonTooltip"
import { downloadVehiclePdf, downloadVehicleStockPdf } from "helpers/pdf_helper"
import { downloadShowroomStockExcel } from "api/stockApi"
import { useResponsiveCollapse } from "hooks/useWindowSize"
import StockFilters from "./StockFilters"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import ImageViewer from "components/Common/ImageViewer/ImageViewer"
import ThemeModal from "components/Common/ThemeModal"
import PublishDraftSwitch from "components/Common/PublishDraftSwitch/PublishDraftSwitch"
import { getStockColumns } from "components/Common/stock-table/stockColumns"
import CarStockWrapper from "pages/VehicleImporterPages/ImporterView/tabs/stocks/components/CarStockList/CarStockWrapper"
import BulkVehicleUploader from "./BulkVehicleUploader"
import BulkStatusPublishModal from "./BulkStatusPublishModal"
import DriveImagePreviewModal from "../StockView/DriveImagePreviewModal"
import BulkDriveImageImportModal from "./BulkDriveImageImportModal"
import DriveSyncModal from "components/Common/DriveSyncModal"
import AuctionSheetExtractModal from "../StockView/AuctionSheetExtractModal"
import { ACCESS } from "constants/accessPermissions"
import { useAuth } from "hooks/authHook"

const StockList = () => {
  document.title = "Stock List"
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const canDownloadShowroomExcelWithImporter = hasPermission(
    ACCESS.STOCK.SHOWROOM_STOCK_EXCEL_WITH_IMPORTER
  )
  const canCreateStock = hasPermission(ACCESS.STOCK.CREATE)

  const [stock, setStock] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [selectedImageUrls, setSelectedImageUrls] = useState([])
  const [imageTitle, setImageTitle] = useState("Auction Sheet")
  const [isShowroomExcelDownloading, setIsShowroomExcelDownloading] =
    useState(false)
  const [showroomExcelModalOpen, setShowroomExcelModalOpen] = useState(false)
  const [drivePreviewOpen, setDrivePreviewOpen] = useState(false)
  const [drivePreviewStock, setDrivePreviewStock] = useState(null)
  const [bulkDriveImportOpen, setBulkDriveImportOpen] = useState(false)
  const [driveSyncModalOpen, setDriveSyncModalOpen] = useState(false)
  const [readyStockUpdatingId, setReadyStockUpdatingId] = useState(null)
  const [auctionSheetOpen, setAuctionSheetOpen] = useState(false)
  const [moreMenuOpen, setMoreMenuOpen] = useState(false)

  const openImageViewer = images => {
    setSelectedImageIndex(0)
    setViewerOpen(true)
    setSelectedImageUrls(
      Array.isArray(images) ? images : [images].filter(Boolean)
    )
  }

  const {
    params,
    setPage,
    resetParams,
    hasActiveFilters,
    updateParams,
  } = useListParams({
    page: 1,
    isShowCount: true,
    reconditioned: 1,
  })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...params,
    }),
    [params]
  )

  const showroomExcelSummaryParams = useMemo(
    () => ({
      page: 1,
      per_page: 1,
      isShowCount: false,
      vs_ready_stock: 1,
      reconditioned: 1,
    }),
    []
  )

  const {
    data: stocksResponse,
    isLoading,
    refetch: stocksRefetch,
    isRefetching,
  } = useVehicleStocks(queryParams)

  const {
    data: showroomExcelSummaryResponse,
    isLoading: isShowroomExcelSummaryLoading,
    isRefetching: isShowroomExcelSummaryRefetching,
  } = useVehicleStocks(showroomExcelSummaryParams, showroomExcelModalOpen)

  const { mutateAsync: deleteStock, isPending: stockDeleteLoading } =
    useDeleteVehicleStock()
  const { mutateAsync: editStock } = useEditVehicleStock()

  const handleAddNewClick = () => {
    navigate("/stock-add-edit")
  }

  const handleStockClick = useCallback(stockData => {
    if (stockData?.stock_id) {
      navigate(`/stock-add-edit/${stockData?.stock_id}`)
    }
  }, [])

  const onClickDelete = useCallback(stockData => {
    setStock(stockData)
    setDeleteModal(true)
  }, [])

  const onPreviewDriveImages = useCallback(stockData => {
    setDrivePreviewStock(stockData)
    setDrivePreviewOpen(true)
  }, [])

  const handleReadyStockToggle = useCallback(
    async (stockData, value) => {
      if (!stockData || !stockData.id) return

      setReadyStockUpdatingId(stockData.id)

      try {
        const res = await editStock({
          id: stockData.id,
          vs_ready_stock: value,
        })

        toast.success(
          res && res.message
            ? res.message
            : value
            ? "Vehicle marked as Ready Stock."
            : "Vehicle marked as Not Ready."
        )
        stocksRefetch()
      } catch (error) {
        console.error("Failed to update ready stock:", error)
        toast.error("Failed to update Ready Stock.")
      } finally {
        setReadyStockUpdatingId(null)
      }
    },
    [editStock, stocksRefetch]
  )

  const handleDeleteStock = useCallback(async () => {
    if (stock?.id) {
      try {
        const res = await deleteStock(stock.id)
        toast.success(res?.message ?? "Stock deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        console.error("Failed to delete stock:", error)
        toast.error("Failed to delete stock.")
      }
    }
  }, [stock, deleteStock])

  const handleListRefresh = () => {
    stocksRefetch()
  }

  const onClickDownload = async stockId => {
    try {
      await downloadVehiclePdf(stockId)
    } catch (error) {}
  }

  const { isMobile, toggleRow, isExpanded } = useResponsiveCollapse()

  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false)
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)

  // ✅ Auto-hide filters on mobile by default
  useEffect(() => {
    if (isMobile) setIsFiltersOpen(false)
    else setIsFiltersOpen(false)
  }, [isMobile])

  const columns = useMemo(
    () =>
      getStockColumns({
        navigate,
        isMobile,
        toggleRow,
        isExpanded,
        handleStockClick,
        onClickDelete,
        onClickDownload,
        openImageViewer,
        setImageTitle,
        onPreviewDriveImages,
        onReadyStockToggle: handleReadyStockToggle,
        readyStockUpdatingId,
      }),
    [
      navigate,
      isMobile,
      toggleRow,
      isExpanded,
      handleStockClick,
      onClickDelete,
      onPreviewDriveImages,
      handleReadyStockToggle,
      readyStockUpdatingId,
    ]
  )

  // ✅ Extracted clean data
  const stocksData = stocksResponse?.data?.vehicles?.data ?? []
  const paginationInfo = stocksResponse?.data?.vehicles ?? {}
  const filteredOptions = stocksResponse?.data?.counts || {}
  const showroomReadyStockTotal =
    showroomExcelSummaryResponse?.data?.vehicles?.total ?? 0

  const handleStockDownload = async () => {
    try {
      await downloadVehicleStockPdf(queryParams)
    } catch (error) {}
  }

  const handleShowroomStockExcelDownload = async (importer = false) => {
    setIsShowroomExcelDownloading(true)

    try {
      await downloadShowroomStockExcel({ importer })
      setShowroomExcelModalOpen(false)
    } catch (error) {
      console.error("Failed to download showroom stock Excel:", error)
      toast.error("Failed to download showroom stock Excel.")
    } finally {
      setIsShowroomExcelDownloading(false)
    }
  }

  const handleReadyStockFilterToggle = value => {
    updateParams(prev => ({
      ...prev,
      vs_ready_stock: value ? 1 : "",
      page: 1,
    }))
  }

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteStock}
        loading={stockDeleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <ImageViewer
        images={selectedImageUrls}
        initialIndex={selectedImageIndex}
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        title={imageTitle}
      />

      <DriveImagePreviewModal
        isOpen={drivePreviewOpen}
        toggle={() => setDrivePreviewOpen(prev => !prev)}
        driveLink={
          drivePreviewStock?.drive_link || drivePreviewStock?.google_drive_link
        }
        stockId={drivePreviewStock?.id}
        onImported={stocksRefetch}
      />

      <BulkDriveImageImportModal
        isOpen={bulkDriveImportOpen}
        toggle={() => setBulkDriveImportOpen(prev => !prev)}
        onImported={stocksRefetch}
      />

      <DriveSyncModal
        isOpen={driveSyncModalOpen}
        toggle={() => setDriveSyncModalOpen(prev => !prev)}
      />

      <AuctionSheetExtractModal
        isOpen={auctionSheetOpen}
        toggle={() => setAuctionSheetOpen(prev => !prev)}
        onOnboarded={stocksRefetch}
      />

      <ThemeModal
        isOpen={showroomExcelModalOpen}
        toggle={() => {
          if (!isShowroomExcelDownloading) {
            setShowroomExcelModalOpen(false)
          }
        }}
        title="Showroom Stock Excel"
        size="md"
        footer={
          <>
            <Button
              color="light"
              type="button"
              onClick={() => setShowroomExcelModalOpen(false)}
              disabled={isShowroomExcelDownloading}
            >
              Cancel
            </Button>
            <Button
              color="success"
              type="button"
              onClick={() => handleShowroomStockExcelDownload(false)}
              disabled={
                isShowroomExcelDownloading ||
                isShowroomExcelSummaryLoading ||
                isShowroomExcelSummaryRefetching ||
                showroomReadyStockTotal === 0
              }
            >
              {isShowroomExcelDownloading ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Generating...
                </>
              ) : (
                <>
                  <i className="bx bx-spreadsheet me-1" />
                  Proceed to Download
                </>
              )}
            </Button>
          </>
        }
      >
        <div className="border rounded p-3 bg-light mb-3">
          <div className="text-muted small text-uppercase">Ready to export</div>
          <div className="h3 mb-0">
            {isShowroomExcelSummaryLoading ||
            isShowroomExcelSummaryRefetching ? (
              <Spinner size="sm" />
            ) : (
              showroomReadyStockTotal
            )}
          </div>
          <div className="text-muted small">vehicles will be included</div>
        </div>

        <div className="mb-3">
          <h6>Which vehicles come in this Excel?</h6>
          <ul className="mb-0 ps-3">
            <li>Only stock records where Ready Stock is enabled.</li>
            <li>The export ignores current page filters and uses all ready stock vehicles.</li>
            <li>Rows are grouped by vehicle Brand, Model, and Engine Type.</li>
            <li>Photo links come from each vehicle stock Google Drive link.</li>
          </ul>
        </div>

        {canDownloadShowroomExcelWithImporter && (
          <div className="border border-warning rounded p-3 bg-warning bg-opacity-10 mb-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
              <div>
                <div className="fw-semibold text-warning-emphasis">
                  Restricted export
                </div>
                <div className="text-muted small">
                  Includes importer names inside the chassis column.
                </div>
              </div>
              <Button
                color="warning"
                outline
                type="button"
                onClick={() => handleShowroomStockExcelDownload(true)}
                disabled={
                  isShowroomExcelDownloading ||
                  isShowroomExcelSummaryLoading ||
                  isShowroomExcelSummaryRefetching ||
                  showroomReadyStockTotal === 0
                }
              >
                {isShowroomExcelDownloading ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="bx bx-lock-alt me-1" />
                    Importer
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {showroomReadyStockTotal === 0 &&
          !isShowroomExcelSummaryLoading &&
          !isShowroomExcelSummaryRefetching && (
            <div className="alert alert-warning mb-0">
              No ready stock vehicles found for showroom Excel export.
            </div>
          )}
      </ThemeModal>

      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Stock" breadcrumbItem="Stock List" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom ">
                  <Row className="justify-content-between align-items-center">
                    <Col xs={12} sm={6}>
                      <h5 className="mb-1">
                        Stock List{" "}
                        <span className="badge badge-soft-info">
                          {paginationInfo?.total ?? 0}
                        </span>
                      </h5>
                      <small className="text-muted">
                        Create, import, and export inventory
                      </small>
                    </Col>
                    <Col xs={12} sm={6}>
                      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-sm-end mt-2 mt-sm-0">
                        <ButtonTooltip
                          type="refresh"
                          btnId="refreshStockList"
                          onClick={handleListRefresh}
                          loading={isRefetching}
                          color="light"
                        />
                        <UncontrolledButtonDropdown
                          direction="start"
                          isOpen={moreMenuOpen}
                          toggle={() => setMoreMenuOpen(prev => !prev)}
                        >
                          <DropdownToggle
                            caret
                            color="light"
                            size="sm"
                            title="More actions"
                          >
                            <i className="bx bx-dots-horizontal-rounded" />
                            <span className="d-none d-sm-inline ms-1">
                              More
                            </span>
                          </DropdownToggle>
                          <DropdownMenu end persist>
                            <BulkVehicleUploader
                              renderTrigger={({ toggle }) => (
                                <DropdownItem
                                  toggle={false}
                                  onClick={() => {
                                    setMoreMenuOpen(false)
                                    toggle()
                                  }}
                                >
                                  <i className="bx bx-upload me-1" />
                                  Bulk Upload
                                </DropdownItem>
                              )}
                            />
                            <BulkStatusPublishModal
                              onUpdated={stocksRefetch}
                              renderTrigger={({ toggle }) => (
                                <DropdownItem
                                  toggle={false}
                                  onClick={() => {
                                    setMoreMenuOpen(false)
                                    toggle()
                                  }}
                                >
                                  <i className="bx bx-edit-alt me-1" />
                                  Bulk Status
                                </DropdownItem>
                              )}
                            />
                            <DropdownItem
                              toggle={false}
                              onClick={() => {
                                setMoreMenuOpen(false)
                                setBulkDriveImportOpen(true)
                              }}
                            >
                              <i className="bx bx-images me-1" />
                              Bulk Drive Images
                            </DropdownItem>
                            <DropdownItem divider />
                            <CarStockWrapper
                              {...params}
                              allowedKeys={[
                                "page",
                                "per_page",
                                "isShowCount",
                              ]}
                              renderTrigger={({ onDownload }) => (
                                <DropdownItem
                                  toggle={false}
                                  onClick={() => {
                                    setMoreMenuOpen(false)
                                    onDownload()
                                  }}
                                >
                                  <i className="bx bx-download me-1" />
                                  Download PDF
                                </DropdownItem>
                              )}
                            />
                            <DropdownItem
                              toggle={false}
                              onClick={() => {
                                setMoreMenuOpen(false)
                                setShowroomExcelModalOpen(true)
                              }}
                              disabled={isShowroomExcelDownloading}
                            >
                              <i className="bx bx-spreadsheet me-1" />
                              Showroom Excel
                            </DropdownItem>
                            <DropdownItem
                              toggle={false}
                              onClick={() => {
                                setMoreMenuOpen(false)
                                setDriveSyncModalOpen(true)
                              }}
                            >
                              <i className="bx bx-cloud-upload me-1" />
                              Showroom Excel Drive Sync
                            </DropdownItem>
                            {hasActiveFilters(["isShowCount"]) && (
                              <>
                                <DropdownItem divider />
                                <DropdownItem
                                  toggle={false}
                                  className="text-danger"
                                  onClick={() => {
                                    setMoreMenuOpen(false)
                                    resetParams()
                                  }}
                                >
                                  <i className="bx bx-x-circle me-1" />
                                  Reset Filters
                                </DropdownItem>
                              </>
                            )}
                          </DropdownMenu>
                        </UncontrolledButtonDropdown>

                        {canCreateStock && (
                          <Button
                            color="success"
                            onClick={() => setAuctionSheetOpen(true)}
                          >
                            <i className="bx bx-file me-1" />
                            Add Vehicle from Auction Sheet
                          </Button>
                        )}
                        <Button color="primary" onClick={handleAddNewClick}>
                          <i className="bx bx-plus me-1" />
                          Add New Stock
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
                <CardBody>
                  <Row>
                    <Col sm={5} lg={3} className="mb-3">
                      <GlobalInputSearch
                        value={params.search}
                        onChange={val =>
                          updateParams(prev => ({
                            ...prev,
                            search: val,
                            page: 1,
                          }))
                        }
                      />
                    </Col>
                    <Col>
                      <div className="mb-2 d-flex flex-wrap gap-2 justify-content-end align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-medium">Ready Stock</span>
                          <PublishDraftSwitch
                            initialValue={Number(params.vs_ready_stock) === 1}
                            trueLabel="Ready"
                            falseLabel="All"
                            trueIcon="bx-check-circle"
                            falseIcon="bx-list-ul"
                            onToggle={handleReadyStockFilterToggle}
                          />
                        </div>
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => setIsFiltersOpen(prev => !prev)}
                        >
                          {isFiltersOpen ? "Hide Filters ▲" : "Show Filters ▼"}
                        </button>
                      </div>
                    </Col>
                  </Row>
                  <StockFilters
                    params={params}
                    updateParams={updateParams}
                    isAdvancedOpen={isAdvancedOpen}
                    isFiltersOpen={isFiltersOpen}
                    setIsAdvancedOpen={setIsAdvancedOpen}
                    data={filteredOptions}
                  />
                  <TableContainer
                    columns={columns}
                    data={stocksData}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || isRefetching}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </>
  )
}

export default StockList
